"use client";

import { useState } from "react";
import {
  Award,
  Calendar,
  Trophy,
  Star,
  ChevronDown,
  ChevronUp,
  Zap,
  Tv,
} from "lucide-react";
import { ContextAnalysis, AwardsData } from "@/hooks/useAIContextAnalysis";

interface Market {
  id: string;
  question: string;
  outcomes: string[];
  outcomePrices: number[];
}

interface AwardsAISidebarProps {
  analysis: ContextAnalysis;
  eventTitle: string;
  markets?: Market[];
}

function getNomineePrice(
  nomineeName: string,
  markets?: Market[]
): number | null {
  if (!markets) return null;
  const nameLower = nomineeName.toLowerCase();
  for (const market of markets) {
    const idx = market.outcomes.findIndex(
      (o) => o.toLowerCase() === nameLower
    );
    if (idx !== -1 && market.outcomePrices[idx] != null) {
      return market.outcomePrices[idx];
    }
    if (
      market.question.toLowerCase().includes(nameLower) &&
      market.outcomePrices[0] != null
    ) {
      return market.outcomePrices[0];
    }
  }
  return null;
}

const oddsColors: Record<string, { bg: string; text: string }> = {
  Favorite: { bg: "bg-yellow-500/20", text: "text-yellow-400" },
  Contender: { bg: "bg-blue-500/20", text: "text-blue-400" },
  Longshot: { bg: "bg-gray-500/20", text: "text-gray-400" },
};

export default function AwardsAISidebar({
  analysis,
  eventTitle,
  markets = [],
}: AwardsAISidebarProps) {
  const [showAllNominees, setShowAllNominees] = useState(false);

  const awards = analysis.awardsData;
  if (!awards) return null;

  const nominees = awards.nominees || [];
  const sortedNominees = [...nominees].sort((a, b) => {
    const priceA = getNomineePrice(a.name, markets);
    const priceB = getNomineePrice(b.name, markets);
    if (priceA !== null && priceB !== null) return priceB - priceA;
    if (priceA !== null) return -1;
    if (priceB !== null) return 1;
    const oddsOrder = ["Favorite", "Contender", "Longshot"];
    return (oddsOrder.indexOf(a.odds || "Longshot")) - (oddsOrder.indexOf(b.odds || "Longshot"));
  });

  const displayNominees = showAllNominees
    ? sortedNominees
    : sortedNominees.slice(0, 8);
  const hasMoreNominees = sortedNominees.length > 8;
  const ceremony = awards.ceremony;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden">
        <div className="px-4 py-3 bg-[#18181D] border-b border-[#1A1A1A]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-gray-400" />
              <span className="text-gray-100 text-sm font-inter-tight font-bold truncate">
                {awards.awardShow}
              </span>
            </div>
            {ceremony?.edition && (
              <span className="text-gray-500 text-xs font-inter-tight flex-shrink-0">
                {ceremony.edition}
              </span>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* Category */}
          {awards.category && (
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 text-xs font-inter-tight">Category</span>
              <span className="text-yellow-400 text-xs font-inter-tight font-bold">
                {awards.category}
              </span>
            </div>
          )}

          {/* Ceremony details */}
          {ceremony && (
            <div className="space-y-2">
              {ceremony.date && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs font-inter-tight">Ceremony</span>
                  <span className="text-white text-xs font-inter-tight font-bold">
                    {ceremony.date}
                  </span>
                </div>
              )}
              {ceremony.venue && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs font-inter-tight">Venue</span>
                  <span className="text-gray-300 text-xs font-inter-tight">
                    {ceremony.venue}
                  </span>
                </div>
              )}
              {ceremony.broadcaster && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs font-inter-tight">Broadcast</span>
                  <div className="flex items-center gap-1">
                    <Tv className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-300 text-xs font-inter-tight">
                      {ceremony.broadcaster}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Nominees */}
      {sortedNominees.length > 0 && (
        <div className="bg-[#18181D] border border-[#1A1A1A] rounded-[5px] overflow-hidden hover:border-gray-500 transition-colors">
          <div className="bg-[#18181D] border-b border-[#1A1A1A] px-4 py-3">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[#FFD700]" />
              <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
                Nominees
              </h3>
              <span className="text-gray-500 text-xs font-inter-tight">
                ({sortedNominees.length})
              </span>
            </div>
          </div>
          <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto scrollbar-hide">
            {displayNominees.map((nominee, idx) => {
              const price = getNomineePrice(nominee.name, markets);
              const rankColors = [
                "text-[#FFD700]",
                "text-[#C0C0C0]",
                "text-[#CD7F32]",
              ];
              const odds = oddsColors[nominee.odds || ""] || oddsColors.Longshot;

              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-2.5 rounded-[5px] bg-[#0f0f12] border border-[#1A1A1A] hover:border-gray-500 transition-colors"
                >
                  {/* Rank */}
                  <span
                    className={`w-5 text-center font-monument font-bold text-sm ${rankColors[idx] || "text-gray-500"}`}
                  >
                    {idx + 1}
                  </span>

                  {/* Color dot */}
                  <div
                    className="w-8 h-8 rounded-[5px] flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: nominee.color || "#6B7280" }}
                  >
                    <Award className="w-4 h-4 text-white" />
                  </div>

                  {/* Name & details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-satoshi font-bold text-sm truncate">
                      {nominee.name}
                    </p>
                    <div className="flex items-center gap-1.5">
                      {nominee.work && (
                        <span className="text-gray-400 text-xs truncate">
                          {nominee.work}
                        </span>
                      )}
                      {nominee.previousWins != null && nominee.previousWins > 0 && (
                        <span className="text-yellow-500 text-[10px] font-inter-tight flex-shrink-0">
                          {nominee.previousWins}x winner
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Price / Odds */}
                  {price !== null ? (
                    <span className="text-white font-monument font-bold text-sm flex-shrink-0">
                      {Math.round(price * 100)}%
                    </span>
                  ) : nominee.odds ? (
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-inter-tight font-bold ${odds.bg} ${odds.text} flex-shrink-0`}
                    >
                      {nominee.odds}
                    </span>
                  ) : null}
                </div>
              );
            })}

            {hasMoreNominees && (
              <button
                onClick={() => setShowAllNominees(!showAllNominees)}
                className="w-full flex items-center justify-center gap-1 py-2 text-gray-400 hover:text-white text-xs font-inter-tight transition-colors"
              >
                {showAllNominees ? (
                  <><ChevronUp className="w-4 h-4" /> Show less</>
                ) : (
                  <><ChevronDown className="w-4 h-4" /> Show {sortedNominees.length - 8} more</>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Historical Note */}
      {awards.historicalNote && (
        <div className="bg-[#0f0f12] border border-[#1A1A1A] rounded-[5px] p-3">
          <div className="flex items-start gap-2">
            <Trophy className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-400 text-[10px] font-inter-tight leading-relaxed">
              {awards.historicalNote}
            </p>
          </div>
        </div>
      )}

      {/* AI Reasoning */}
      {analysis.reasoning && (
        <details className="group">
          <summary className="flex items-center gap-2 text-gray-500 text-xs font-inter-tight cursor-pointer hover:text-gray-300 transition-colors">
            <Zap className="w-3 h-3" />
            <span>Why this widget?</span>
          </summary>
          <p className="mt-2 text-gray-400 text-xs leading-relaxed pl-5">
            {analysis.reasoning}
          </p>
        </details>
      )}
    </div>
  );
}
